import cookie from "react-cookies";
import constants from "../Constants";

const academyURL = constants.baseURL;
const hqURL = constants.hqApiURL || constants.baseURL;
const STAFF_COOKIE = "staffHqKey";
const STAFF_PROFILE = "staffHqProfile";

/** Careers-only session. Never set domain to parent qiktruck.co.za. */
const cookieOpts = () => ({
  path: "/",
  maxAge: 60 * 60 * 12,
  secure: window.location.protocol === "https:",
  sameSite: "Lax",
});

export function getStaffToken() {
  return cookie.load(STAFF_COOKIE) || "";
}

export function getStaffProfile() {
  try {
    const raw = cookie.load(STAFF_PROFILE);
    if (!raw) return null;
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return null;
  }
}

export function setStaffSession(token, profile = null) {
  if (!token) {
    cookie.remove(STAFF_COOKIE, { path: "/" });
    cookie.remove(STAFF_PROFILE, { path: "/" });
    return;
  }
  const opts = cookieOpts();
  cookie.save(STAFF_COOKIE, token, opts);
  if (profile) {
    cookie.save(STAFF_PROFILE, JSON.stringify(profile), opts);
  }
}

export function clearStaffSession() {
  setStaffSession("");
}

/** @deprecated use setStaffSession */
export function setStaffToken(token) {
  setStaffSession(token);
}

async function staffFetch(path, options = {}) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  const token = getStaffToken();
  try {
    const res = await fetch(`${academyURL}${path}`, {
      ...options,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: token } : {}),
        ...options.headers,
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Request failed");
    return data;
  } finally {
    clearTimeout(timeout);
  }
}

async function hqFetch(path, body) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);
  try {
    const res = await fetch(`${hqURL}${path}`, {
      method: "POST",
      signal: controller.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(
        data.message || data.error || "Login failed. Check your details and try again."
      );
    }
    return data;
  } finally {
    clearTimeout(timeout);
  }
}

export const requestStaffOtp = (idnumber) =>
  hqFetch("/admin-otp", { idnumber: String(idnumber || "").trim() });

export const validateStaffOtp = async (idnumber, otp) => {
  const data = await hqFetch("/validate-admin-otp", {
    idnumber: String(idnumber || "").trim(),
    otp: String(otp || "").trim(),
  });
  const token = data["hq-id-qt"];
  if (!token) throw new Error("Login failed. No session returned.");
  setStaffSession(token, {
    adminid: data.adminid,
    email: data.email,
    firstname: data.firstname,
    lastname: data.lastname,
    mobilenumber: data.mobilenumber,
  });
  return data;
};

/** Redeem a one-time HQ → careers staff handoff token. */
export const redeemStaffHandoff = async (handoffToken) => {
  const data = await staffFetch("/hq/academy/staff-handoff/redeem", {
    method: "POST",
    body: JSON.stringify({ handoff_token: String(handoffToken || "").trim() }),
  });
  const token = data["hq-id-qt"] || data.token;
  if (!token) throw new Error("Handoff failed. No session returned.");
  setStaffSession(token, {
    adminid: data.adminid,
    email: data.email,
    firstname: data.firstname,
    lastname: data.lastname,
    mobilenumber: data.mobilenumber,
  });
  return data;
};

export const getStaffAcademyMe = () => staffFetch("/hq/academy/me");
export const getStaffAcademyModule = (moduleSlug) =>
  staffFetch(`/hq/academy/modules/${moduleSlug}`);
export const postStaffAcademyProgress = (body) =>
  staffFetch("/hq/academy/progress", { method: "POST", body: JSON.stringify(body) });
export const getStaffAcademyQuiz = (quizId) => staffFetch(`/hq/academy/quiz/${quizId}`);
export const submitStaffAcademyQuiz = (quizId, answers) =>
  staffFetch(`/hq/academy/quiz/${quizId}/submit`, {
    method: "POST",
    body: JSON.stringify({ answers }),
  });
